import type { $Fetch } from 'ofetch';
import type {
  CertificadoGateway, ListCertificadosParams,
} from '@/application/port/out/CertificadoGateway';
import type { Certificado, RevogarCertificadoCommand } from '@/domain/model/Certificado';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/**
 * HTTP gateway pra Certificado. Authority varia por endpoint:
 * - read|listByAluno|get|marcarBaixado: acadadmin:certificado:read
 * - revogar: acadadmin:certificado:revogar
 *
 * @since G.5.8
 */
export class HttpCertificadoGateway implements CertificadoGateway {
  constructor(private readonly client: $Fetch) {}

  async list(params: ListCertificadosParams): Promise<Page<Certificado>> {
    const query: Record<string, string | number> = {
      page: params.page ?? 0,
      size: params.size ?? 50,
    };
    if (params.status) query.status = params.status;
    if (params.categoria) query.categoria = params.categoria;
    if (params.courseId) query.courseId = params.courseId;
    const res = await this.client<ApiResponse<Page<Certificado>>>(
      '/api/v1/certificados', { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }

  async listByAluno(alunoId: string): Promise<Certificado[]> {
    const res = await this.client<ApiResponse<Certificado[]>>(
      `/api/v1/certificados/aluno/${encodeURIComponent(alunoId)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async get(id: string): Promise<Certificado> {
    const res = await this.client<ApiResponse<Certificado>>(
      `/api/v1/certificados/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async marcarBaixado(id: string): Promise<Certificado> {
    const res = await this.client<ApiResponse<Certificado>>(
      `/api/v1/certificados/${encodeURIComponent(id)}/baixado`, { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }

  async revogar(id: string, cmd: RevogarCertificadoCommand): Promise<Certificado> {
    const res = await this.client<ApiResponse<Certificado>>(
      `/api/v1/certificados/${encodeURIComponent(id)}/revogar`,
      { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
}
